import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import { supabase } from '../lib/supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiBookOpen, FiSearch, FiFilter, FiHeart, FiShare2, FiCopy, FiCheck, FiPlus,
  FiGrid, FiList, FiStar, FiX, FiChevronDown, FiUsers, FiTarget, FiZap,
  FiShield, FiBulb, FiTrendingUp, FiRefreshCw, FiEye, FiClock, FiAward,
  FiMessageCircle, FiSettings, FiHome, FiGlobe, FiActivity, FiMonitor,
  FiPenTool, FiBriefcase, FiCompass, FiFeather, FiLayers, FiMusic, FiSun
} = FiIcons;

const QuotesBrowser = () => {
  const { user } = useAuth();
  const { addNotification } = useApp();

  // State management
  const [categories, setCategories] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [likedQuotes, setLikedQuotes] = useState(new Set());
  const [sortBy, setSortBy] = useState('recent'); // 'recent', 'popular', 'alphabetical'

  // Enhanced categories with better visual organization
  const enhancedCategories = [
    {
      id: 'inspiration',
      name: 'Inspiration',
      icon: 'FiSun',
      color: '#F59E0B',
      description: 'Quotes that motivate and uplift your spirit'
    },
    {
      id: 'education',
      name: 'Education',
      icon: 'FiBookOpen',
      color: '#3B82F6',
      description: 'Wisdom about learning, knowledge and growth'
    },
    {
      id: 'leadership',
      name: 'Leadership',
      icon: 'FiTarget',
      color: '#10B981',
      description: 'Insights on leading others and personal development'
    },
    {
      id: 'success',
      name: 'Success',
      icon: 'FiAward',
      color: '#8B5CF6',
      description: 'Guidance on achievement and overcoming obstacles'
    },
    {
      id: 'wisdom',
      name: 'Wisdom',
      icon: 'FiFeather',
      color: '#EC4899',
      description: 'Timeless insights and philosophical reflections'
    },
    {
      id: 'community',
      name: 'Community',
      icon: 'FiUsers',
      color: '#F97316',
      description: 'Thoughts on cooperation and social harmony'
    },
    {
      id: 'creativity',
      name: 'Creativity',
      icon: 'FiPenTool',
      color: '#06B6D4',
      description: 'Insights on innovation and artistic expression'
    },
    {
      id: 'resilience',
      name: 'Resilience',
      icon: 'FiZap',
      color: '#EF4444',
      description: 'Quotes about perseverance and overcoming challenges'
    },
  ];

  // Icon mapping for categories
  const iconMap = {
    'FiBookOpen': FiBookOpen,
    'FiUsers': FiUsers,
    'FiTarget': FiTarget,
    'FiShield': FiShield,
    'FiBulb': FiBulb,
    'FiHeart': FiHeart,
    'FiTrendingUp': FiTrendingUp,
    'FiStar': FiStar,
    'FiZap': FiZap,
    'FiRefreshCw': FiRefreshCw,
    'FiEye': FiEye,
    'FiClock': FiClock,
    'FiAward': FiAward,
    'FiMessageCircle': FiMessageCircle,
    'FiSettings': FiSettings,
    'FiHome': FiHome,
    'FiGlobe': FiGlobe,
    'FiActivity': FiActivity,
    'FiMonitor': FiMonitor,
    'FiPenTool': FiPenTool,
    'FiBriefcase': FiBriefcase,
    'FiCompass': FiCompass,
    'FiFeather': FiFeather,
    'FiLayers': FiLayers,
    'FiMusic': FiMusic,
    'FiSun': FiSun
  };

  // Load categories and quotes on component mount
  useEffect(() => {
    loadData();
  }, []);

  // Filter quotes when dependencies change
  useEffect(() => {
    filterQuotes();
  }, [quotes, selectedCategory, searchQuery, sortBy]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('quotes_categories_ilm2024')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (categoriesError) throw categoriesError;

      // Merge with enhanced categories for better presentation
      let mergedCategories = categoriesData || [];
      if (mergedCategories.length === 0) {
        // If no categories from database, use enhanced categories
        mergedCategories = enhancedCategories.map(cat => ({
          id: cat.id,
          name: cat.name,
          icon: cat.icon,
          color: cat.color,
          description: cat.description,
          is_active: true
        }));
      } else {
        // Enhance existing categories with better visual elements
        mergedCategories = mergedCategories.map(dbCat => {
          const enhancedCat = enhancedCategories.find(e => 
            e.name.toLowerCase() === dbCat.name.toLowerCase() || 
            e.id === dbCat.id
          );
          
          return {
            ...dbCat,
            icon: enhancedCat?.icon || 'FiStar',
            color: enhancedCat?.color || '#6366F1',
            description: enhancedCat?.description || dbCat.description || 'Inspirational quotes'
          };
        });
      }

      // Load quotes with category information
      const { data: quotesData, error: quotesError } = await supabase
        .from('quotes_collection_ilm2024')
        .select(`
          *,
          category:quotes_categories_ilm2024(*)
        `)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (quotesError) throw quotesError;

      setCategories(mergedCategories);
      setQuotes(quotesData || []);

    } catch (error) {
      console.error('Error loading quotes data:', error);
      addNotification({
        type: 'error',
        title: 'Loading Error',
        message: 'Failed to load quotes. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const filterQuotes = () => {
    let filtered = [...quotes];

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(quote => 
        quote.category?.name === selectedCategory ||
        quote.category?.id === selectedCategory
      );
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(quote => 
        quote.text.toLowerCase().includes(query) || 
        quote.author.toLowerCase().includes(query) || 
        quote.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }

    // Sort quotes
    switch (sortBy) {
      case 'popular':
        filtered.sort((a, b) => (b.likes_count || 0) - (a.likes_count || 0));
        break;
      case 'alphabetical':
        filtered.sort((a, b) => a.author.localeCompare(b.author));
        break;
      case 'recent':
      default:
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
    }

    setFilteredQuotes(filtered);
  };

  const handleLikeQuote = async (quoteId) => {
    try {
      const isLiked = likedQuotes.has(quoteId);
      const newLikesCount = isLiked ? -1 : 1;

      // Update local state immediately for better UX
      const newLikedQuotes = new Set(likedQuotes);
      if (isLiked) {
        newLikedQuotes.delete(quoteId);
      } else {
        newLikedQuotes.add(quoteId);
      }
      setLikedQuotes(newLikedQuotes);

      // Update quotes state
      setQuotes(prevQuotes => 
        prevQuotes.map(quote => 
          quote.id === quoteId ? 
            { ...quote, likes_count: (quote.likes_count || 0) + newLikesCount } : 
            quote
        )
      );

      // Update database
      const { error } = await supabase
        .from('quotes_collection_ilm2024')
        .update({ likes_count: supabase.sql`likes_count + ${newLikesCount}` })
        .eq('id', quoteId);

      if (error) throw error;

    } catch (error) {
      console.error('Error updating quote likes:', error);
      // Revert local state on error
      loadData();
    }
  };

  const handleCopyQuote = (quote) => {
    const text = `"${quote.text}" - ${quote.author}`;
    navigator.clipboard.writeText(text).then(() => {
      addNotification({
        type: 'success',
        title: 'Copied!',
        message: 'Quote copied to clipboard'
      });
    });
  };

  const handleShareQuote = (quote) => {
    const text = `"${quote.text}" - ${quote.author}`;
    if (navigator.share) {
      navigator.share({
        title: 'Inspiring Quote',
        text: text
      });
    } else {
      handleCopyQuote(quote);
    }
  };

  const getIconComponent = (iconName) => {
    return iconMap[iconName] || FiStar;
  };

  const getCategoryColor = (category) => {
    if (!category) return '#6366F1'; // Default color
    
    const foundCategory = categories.find(c => 
      c.id === category.id || c.name === category.name
    );
    
    return foundCategory?.color || '#6366F1';
  };

  if (loading) {
    return (
      <div className="space-y-6 px-4 py-4">
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 px-4 py-4 pb-16 md:pb-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
      >
        <div className="text-center">
          <SafeIcon icon={FiBookOpen} className="w-16 h-16 mx-auto mb-4 text-primary-600" />
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Quotes Collection</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Discover inspiring quotes from great minds across {categories.length} categories. 
            Find wisdom, motivation, and guidance for your journey.
          </p>
        </div>
      </motion.div>

      {/* Search and Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
      >
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search quotes, authors, or tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Filter Controls */}
          <div className="flex items-center space-x-4">
            {/* View Mode Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
              >
                <SafeIcon icon={FiGrid} className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
              >
                <SafeIcon icon={FiList} className="w-4 h-4" />
              </button>
            </div>

            {/* Sort Dropdown */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="recent">Most Recent</option>
              <option value="popular">Most Popular</option>
              <option value="alphabetical">Author A-Z</option>
            </select>

            {/* Filter Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <SafeIcon icon={FiFilter} className="w-4 h-4" />
              <span>Categories</span>
              <SafeIcon
                icon={FiChevronDown}
                className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`}
              />
            </button>
          </div>
        </div>

        {/* Categories Filter */}
        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            className="mt-4 pt-4 border-t border-gray-200 overflow-hidden"
          >
            {/* Category Grid View - More visual and organized */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`p-4 rounded-lg transition-all flex flex-col items-center justify-center text-center h-24 ${
                  selectedCategory === 'all' 
                    ? 'bg-primary-50 border-2 border-primary-500 text-primary-700' 
                    : 'bg-gray-50 border border-gray-200 text-gray-700 hover:bg-gray-100'
                }`}
              >
                <SafeIcon icon={FiGlobe} className="w-6 h-6 mb-2" />
                <span className="font-medium">All Categories</span>
                <span className="text-xs mt-1">({quotes.length})</span>
              </button>
              
              {categories.map((category) => {
                const IconComponent = getIconComponent(category.icon);
                const categoryQuotes = quotes.filter(q => 
                  q.category?.name === category.name || q.category?.id === category.id
                );
                
                const categoryColor = category.color || '#6366F1';
                const iconStyle = { color: categoryColor };
                const bgStyle = { backgroundColor: `${categoryColor}10` }; // 10% opacity
                const borderStyle = selectedCategory === category.id || selectedCategory === category.name
                  ? { borderColor: categoryColor, borderWidth: '2px' }
                  : {};
                
                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id || category.name)}
                    className={`p-4 rounded-lg transition-all flex flex-col items-center justify-center text-center h-24 border ${
                      selectedCategory === category.id || selectedCategory === category.name
                        ? 'bg-white shadow-md'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
                    style={{ ...borderStyle, ...bgStyle }}
                  >
                    <SafeIcon icon={IconComponent} className="w-6 h-6 mb-2" style={iconStyle} />
                    <span className="font-medium" style={{ color: categoryColor }}>{category.name}</span>
                    <span className="text-xs mt-1 text-gray-500">({categoryQuotes.length})</span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Category Description - Show when a category is selected */}
      {selectedCategory !== 'all' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
        >
          {categories
            .filter(cat => cat.id === selectedCategory || cat.name === selectedCategory)
            .map(category => {
              const IconComponent = getIconComponent(category.icon);
              const categoryColor = category.color || '#6366F1';
              const bgStyle = { backgroundColor: `${categoryColor}10` };
              
              return (
                <div key={category.id} className="flex items-start">
                  <div 
                    className="p-3 rounded-full mr-4 flex-shrink-0"
                    style={bgStyle}
                  >
                    <SafeIcon 
                      icon={IconComponent} 
                      className="w-6 h-6" 
                      style={{ color: categoryColor }}
                    />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 mb-1">{category.name}</h2>
                    <p className="text-gray-600">{category.description}</p>
                  </div>
                </div>
              );
            })}
        </motion.div>
      )}

      {/* Results Summary */}
      <div className="text-sm text-gray-600">
        Showing {filteredQuotes.length} of {quotes.length} quotes
        {selectedCategory !== 'all' && ` in "${
          categories.find(c => c.id === selectedCategory || c.name === selectedCategory)?.name || selectedCategory
        }"`}
        {searchQuery && ` matching "${searchQuery}"`}
      </div>

      {/* Quotes Display */}
      <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
        {filteredQuotes.map((quote, index) => {
          const categoryColor = getCategoryColor(quote.category);
          const IconComponent = getIconComponent(quote.category?.icon || 'FiStar');
          const isLiked = likedQuotes.has(quote.id);
          
          return (
            <motion.div
              key={quote.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden ${
                viewMode === 'list' ? 'p-4' : 'p-6'
              }`}
            >
              {/* Quote Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div 
                    className="p-2 rounded-full" 
                    style={{ backgroundColor: `${categoryColor}20` }}
                  >
                    <SafeIcon 
                      icon={IconComponent} 
                      className="w-4 h-4" 
                      style={{ color: categoryColor }}
                    />
                  </div>
                  <div>
                    <span 
                      className="text-xs font-medium px-2 py-1 rounded-full" 
                      style={{ backgroundColor: `${categoryColor}20`, color: categoryColor }}
                    >
                      {quote.category?.name || 'Inspirational'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-1">
                  {quote.difficulty_level && (
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      quote.difficulty_level === 'beginner' ? 'bg-green-100 text-green-700' :
                      quote.difficulty_level === 'intermediate' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {quote.difficulty_level}
                    </span>
                  )}
                </div>
              </div>

              {/* Quote Content */}
              <div className="mb-4">
                <blockquote className="text-gray-800 font-medium leading-relaxed mb-3">
                  "{quote.text}"
                </blockquote>
                <cite className="text-primary-600 font-medium">— {quote.author}</cite>
              </div>

              {/* Tags */}
              {quote.tags && quote.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-4">
                  {quote.tags.slice(0, 3).map((tag, tagIndex) => (
                    <span key={tagIndex} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                      #{tag}
                    </span>
                  ))}
                  {quote.tags.length > 3 && (
                    <span className="text-xs text-gray-500">+{quote.tags.length - 3} more</span>
                  )}
                </div>
              )}

              {/* Quote Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => handleLikeQuote(quote.id)}
                    className={`flex items-center space-x-1 transition-colors ${
                      isLiked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
                    }`}
                  >
                    <SafeIcon icon={FiHeart} className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
                    <span className="text-sm">{quote.likes_count || 0}</span>
                  </button>
                  <span className="text-gray-300">•</span>
                  <span className="text-xs text-gray-500">
                    {quote.views_count || 0} views
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleCopyQuote(quote)}
                    className="p-2 text-gray-500 hover:text-primary-600 rounded-lg hover:bg-gray-100 transition-colors"
                    title="Copy quote"
                  >
                    <SafeIcon icon={FiCopy} className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleShareQuote(quote)}
                    className="p-2 text-gray-500 hover:text-primary-600 rounded-lg hover:bg-gray-100 transition-colors"
                    title="Share quote"
                  >
                    <SafeIcon icon={FiShare2} className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* No Results */}
      {filteredQuotes.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-12"
        >
          <SafeIcon icon={FiSearch} className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">No quotes found</h3>
          <p className="text-gray-600 mb-6">
            Try adjusting your search terms or browse different categories.
          </p>
          <button
            onClick={() => {
              setSearchQuery('');
              setSelectedCategory('all');
              setShowFilters(false);
            }}
            className="bg-primary-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-600 transition-colors"
          >
            Clear Filters
          </button>
        </motion.div>
      )}

      {/* Admin Add Quote Button */}
      {user?.isAdmin && (
        <div className="fixed bottom-24 right-4 z-40">
          <button
            onClick={() => window.location.href='/#/admin'}
            className="bg-primary-500 text-white p-4 rounded-full shadow-lg hover:bg-primary-600 transition-colors"
            title="Add new quotes (Admin)"
          >
            <SafeIcon icon={FiPlus} className="w-6 h-6" />
          </button>
        </div>
      )}
    </div>
  );
};

export default QuotesBrowser;